import { createContext, ReactNode, useContext, useEffect } from 'react';
import axios, { AxiosInstance } from 'axios';
import { ToastContext, TShow, TShowProgress, TToastAction } from './ToastContext.context';
import { AuthContext } from '@modules/users';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import config from '@config/index';
import { toApiException } from '@shared/helpers';
import { TApiException } from '@shared/types';

// @ts-ignore
export const ApiContext = createContext<TApiContext>();

type TProps = {
  children: ReactNode;
};
type TUseHandleQueryError = (isError: boolean, err: unknown, log?: string, message?: string | boolean) => void;
type THandleApiErrorOpts = { action?: (apiErr: TApiException) => TToastAction | undefined };
type THandleApiError = (
  log?: string,
  message?: string | boolean,
  opts?: THandleApiErrorOpts
) => (err: unknown, variables: unknown, context?: unknown) => Promise<unknown> | unknown;
type TypeHandleSuccessMessage = (message: string) => () => void;
type TRetry = (failureCount: number, error: any) => boolean;
type TGetAzureBlobUrl = (containerName: string, blobName: string, token: string) => string;
type TApiContext = {
  apiClient: AxiosInstance;
  azureClient: AxiosInstance;
  queryClient: QueryClient;
  useHandleQueryError: TUseHandleQueryError;
  handleSuccessMessage: TypeHandleSuccessMessage;
  handleApiError: THandleApiError;
  show: TShow;
  showProgress: TShowProgress;
  retry: TRetry;
  getAzureBlobUrl: TGetAzureBlobUrl;
};

const apiClient = axios.create({
  baseURL: config.BACKEND_URI,
  headers: {
    'Content-Type': 'application/json',
  },
});
const azureClient = axios.create({ baseURL: config.AZURE_STORAGE_BASE_URL });

export const ApiProvider = ({ children }: TProps) => {
  const { show, showProgress } = useContext(ToastContext);
  const { getAccessToken } = useContext(AuthContext);

  const handleApiErrorBasic = (err: unknown, log?: string, message?: string | boolean, opts?: THandleApiErrorOpts) => {
    message = message === true ? log : message;
    if (!message && message !== false) message = 'Action failed!';

    const apiError = toApiException(err);
    if (apiError) {
      if (apiError.message && message !== false) message = apiError.message;

      console.log(err); // only log to console (already logged by server)
    } else {
      log ? console.error(log, err) : console.error(err);
    }

    if (message) {
      const action = apiError && opts?.action ? opts.action(apiError) : undefined;
      show('error', message, action ? { action } : undefined);
    }
  };

  const useHandleQueryError: TUseHandleQueryError = (isError, err, log, message) => {
    useEffect(() => {
      if (isError) {
        handleApiErrorBasic(err, log, message);
      }
    }, [isError]);
  };

  const handleApiError: THandleApiError = (log, message, opts) => (err, variables, context) => {
    handleApiErrorBasic(err, log, message, opts);
  };
  const handleSuccessMessage: TypeHandleSuccessMessage = (message) => () => show('success', message);

  const retry: TRetry = (failureCount, error) => {
    return failureCount < 3 && error?.response?.data?.type !== 'api';
  };

  const getAzureBlobUrl: TGetAzureBlobUrl = (containerName, blobName, token) =>
    `${config.AZURE_STORAGE_BASE_URL}/${containerName}/${encodeURIComponent(blobName)}?${token}`;

  apiClient.interceptors.request.use(
    async (config) => {
      const accessToken = await getAccessToken();
      // @ts-ignore
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
      };

      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  return (
    <ApiContext.Provider
      value={{
        apiClient,
        azureClient,
        queryClient: useQueryClient(),
        useHandleQueryError,
        handleApiError,
        handleSuccessMessage,
        show,
        showProgress,
        retry,
        getAzureBlobUrl,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};
